import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals, Primary, SemanticError } from '../../color-palette/colors';
import { FOCUS_RING_OFFSET, focusRing } from '../../focusRing';

import { backspaceAt, pasteAt, typeAt } from './codeEdits';
import type { CodeEdit } from './codeEdits';

export type CodeInputAlign = 'start' | 'center';

export interface CodeInputProps {
  readonly label: string;
  readonly labelledBy?: string;
  readonly describedBy?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly length: number;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly invalid?: boolean;
  readonly errorId?: string;
  readonly onComplete?: (value: string) => void;
  readonly autoFocus?: boolean;
  readonly align?: CodeInputAlign;
  readonly shake?: boolean;
}

const shakeKeyframes = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-4%); }
  40%, 80% { transform: translateX(4%); }
`;

const groupSx =
  (align: CodeInputAlign, shake: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    display: 'flex',
    gap: 3,
    justifyContent: align === 'center' ? 'center' : 'flex-start',
    animation: shake ? `${shakeKeyframes} ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}` : 'none',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  });

const boxSx =
  (invalid: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    flex: '1 1 0',
    minInlineSize: theme.controlHeight.medium,
    maxInlineSize: theme.spacing(9),
    aspectRatio: '1 / 1',
    boxSizing: 'border-box',
    padding: 0,
    textAlign: 'center',
    border: '1px solid',
    borderColor: invalid ? SemanticError[600] : Neutrals[500],
    borderRadius: 2,
    backgroundColor: theme.palette.background.paper,
    color: Neutrals[900],
    fontSize: theme.typography.title.fontSize,
    fontWeight: theme.typography.cardTitle.fontWeight,
    fontVariantNumeric: 'tabular-nums',
    outline: 'none',
    transition: theme.transitions.create(['border-color'], { duration: theme.transitions.duration.shortest }),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '&:focus': {
      outline: focusRing(invalid ? SemanticError[600] : Primary[500]),
      outlineOffset: FOCUS_RING_OFFSET,
    },
    '&:disabled': {
      backgroundColor: Neutrals[50],
      color: Neutrals[400],
      cursor: 'not-allowed',
    },
  });

const selectBox = (event: React.FocusEvent<HTMLInputElement>): void => event.currentTarget.select();

export const CodeInput: React.FC<CodeInputProps> = ({
  label,
  labelledBy,
  describedBy,
  value,
  onChange,
  length,
  disabled = false,
  readOnly = false,
  invalid = false,
  errorId,
  onComplete,
  autoFocus = false,
  align = 'start',
  shake = false,
}) => {
  const { t } = useTranslation();
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const commit = (edit: CodeEdit | null): void => {
    if (edit === null || readOnly) return;
    onChange(edit.next);
    const box = inputs.current[edit.focus];
    box?.focus();
    box?.select();
    if (edit.next.length === length) onComplete?.(edit.next);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Backspace') return;
    event.preventDefault();
    commit(backspaceAt(value, index));
  };

  const handlePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    commit(pasteAt(value, index, event.clipboardData.getData('text'), length));
  };

  return (
    <Box
      role="group"
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : label}
      aria-describedby={describedBy}
      sx={groupSx(align, shake)}
    >
      {Array.from({ length }, (_, index) => (
        <Box
          key={index}
          component="input"
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          ref={(el: HTMLInputElement | null) => {
            inputs.current[index] = el;
          }}
          aria-label={t('common.codeInput.digit', 'Digit {{n}} of {{total}}', { n: index + 1, total: length })}
          aria-invalid={invalid || undefined}
          aria-describedby={errorId}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus && index === 0}
          value={value[index] ?? ''}
          onFocus={selectBox}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => commit(typeAt(value, index, event.target.value, length))}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, event)}
          onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handlePaste(index, event)}
          sx={boxSx(invalid)}
        />
      ))}
    </Box>
  );
};
